/**
 * authInterceptor — attaches a response interceptor to an axios instance that
 * triggers global logout on 401 responses (except /auth/login which handles
 * bad credentials locally).
 *
 * Usage:
 *   import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
 *   attachAuthInterceptors(apiClient);
 *
 * Design:
 * - On 401 (or error code matching AUTH-401-*): clears stored tokens and
 *   emits a SESSION_EXPIRED event via sessionEvents.
 * - sessionEvents notifies the React AuthLayout subscriber which then
 *   navigates to /login?expired=1&returnUrl=... and shows a banner.
 * - Falls back to window.location.assign if no subscriber is registered.
 * - Idempotent: subsequent 401s after the first do not trigger duplicate
 *   redirects (guarded by a redirecting flag that resets on navigation).
 * - Excluded paths:
 *   - /auth/login  → bad password, handled locally
 *   - /auth/refresh → handled by useSessionRefresh directly
 */

import type { AxiosInstance } from 'axios';
import { clearStoredTokens } from '../../login/api/loginApi';
import { sessionEvents } from './sessionEvents';

// Module-level flag: once we start redirecting, suppress further triggers
// until the page navigates (and this module re-initialises on reload).
let redirecting = false;

/**
 * Returns true when the request URL should NOT trigger global logout even on
 * a 401 response.
 *
 * Bypassed paths:
 * - /auth/login  — bad credentials handled locally by the login form
 * - /auth/refresh — handled by useSessionRefresh; it decides to logout
 * - /simulate/   — error-demo endpoints that intentionally return 4xx/5xx
 *                  to demonstrate error handling; the 401 there is not a
 *                  session expiry signal
 * - /showcase/exception-demo — the capability catalog's ErrorCode demo (POST). It throws
 *                  the selected ErrorCode on purpose, so AUTH-401-001 must render
 *                  in the result card instead of logging the user out.
 *                  (2026-07-14: the demo used to kick users to /login?expired=1,
 *                  which is why the R11 regression test failed for months.)
 */
function isBypassedPath(url: string | undefined): boolean {
  if (!url) return false;
  if (url.includes('/auth/login')) return true;
  if (url.includes('/auth/refresh')) return true;
  if (url.includes('/simulate/')) return true;
  if (url.includes('/showcase/exception-demo')) return true;
  return false;
}

/**
 * Checks whether an axios error looks like an auth-expiry 401.
 * We match HTTP 401 status OR the ERAF structured error code AUTH-401-*.
 */
function isAuthExpiry401(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;

  // Standard axios error: check HTTP status
  const status = (e['response'] as Record<string, unknown> | undefined)?.['status'];
  if (status === 401) return true;

  // Already-unwrapped ERAF structured error (after response interceptors run)
  const code = e['code'] as string | undefined;
  if (code && code.startsWith('AUTH-401-')) return true;

  return false;
}

/**
 * Attaches the global session-expiry response interceptor to the given
 * axios instance. Safe to call multiple times on different instances; each
 * gets its own interceptor registration.
 */
export function attachAuthInterceptors(client: AxiosInstance): void {
  client.interceptors.response.use(
    // Success path — pass through unchanged
    (response) => response,

    // Error path — check for session expiry
    (error: unknown) => {
      const axiosError = error as { config?: { url?: string } };
      const requestUrl = axiosError?.config?.url;

      if (!redirecting && isAuthExpiry401(error) && !isBypassedPath(requestUrl)) {
        redirecting = true;

        // Clear tokens synchronously so AuthGuard also blocks re-entry
        clearStoredTokens();

        // Emit to React subscriber (AuthLayout) or fall back to window.location
        const returnUrl =
          typeof window !== 'undefined' ? window.location.pathname : '/';
        sessionEvents.emit({ type: 'SESSION_EXPIRED', returnUrl });

        // Reset flag after a tick to allow test cleanup; in production the
        // page navigates away so this never fires.
        setTimeout(() => {
          redirecting = false;
        }, 100);
      }

      return Promise.reject(error);
    }
  );
}
